import { computed } from 'vue';

import { canCreateSubscriptionsByRole } from '@/domains/permissions/role/subscriptions';
import { getShortPlan } from '@/contexts/billingContext/domain/getShortPlan';
import { EBillingPlanCode } from '@/contexts/billingContext/domain/constants';
import { formatDate } from '@/utils/dateUtils';
import { DATE_FORMAT } from '@/constants';
import { TSubscription } from '@/contexts/billingContext/domain/types';
import { useTenantsStore } from '@/stores/tenants';
import { getIsAdditionalPlanCode } from '@/contexts/billingContext/domain/getIsAdditionalPlanCode';
import { getIsCustomCargoOwnerPlanCode } from '@/contexts/billingContext/domain/getIsCustomCargoOwnerPlanCode';

export const useView = () => {
  const tenantsStore = useTenantsStore();
  const subscriptionRequest = computed(() => tenantsStore.currentTenantDetails?.subscriptionRequest);
  const subscriptions = computed(() => tenantsStore.currentTenantDetails?.subscriptions || []);
  const subscriptionWithPendingStatus = computed(() => tenantsStore.getPendingSubscription);

  const shortPlan = computed(() => {
    const planCode = subscriptionWithPendingStatus.value?.planCode;
    if (!planCode) return '';

    if (!getIsAdditionalPlanCode(planCode) && !getIsCustomCargoOwnerPlanCode(planCode)) {
      return getShortPlan(planCode as EBillingPlanCode);
    }

    if (subscriptionRequest.value?.planCode) {
      return getShortPlan(subscriptionRequest.value.planCode);
    }

    return '';
  },
  );

  const hasCarrierStartSubscription = computed(
    () => subscriptions.value.find(
      (subscription: TSubscription) => subscription.planCode === EBillingPlanCode.carrierStart,
    ),
  );

  const сanCreateSubscriptions = computed(() => canCreateSubscriptionsByRole());

  const isCurrentTenantWithoutSubscription = computed(() => !subscriptions.value.length && !subscriptionRequest.value);

  const showPlanSelectionButton = computed(
    () => сanCreateSubscriptions.value
    && (isCurrentTenantWithoutSubscription.value || hasCarrierStartSubscription.value),
  );

  const hasCargoOwnerSubscription = computed(
    () => !!(subscriptionRequest.value?.planCode === EBillingPlanCode.cargoOwner
      || subscriptionWithPendingStatus.value?.planCode === EBillingPlanCode.cargoOwner
      || getIsCustomCargoOwnerPlanCode(subscriptionWithPendingStatus.value?.planCode)),
  );

  const expectedSubscriptionDate = computed(() => {
    if (subscriptionWithPendingStatus.value) {
      return formatDate(subscriptionWithPendingStatus.value.subscriptionAt || '', { outputFormat: DATE_FORMAT.dayMonthYear });
    }
    return formatDate(subscriptionRequest.value?.subscriptionAt || '', { outputFormat: DATE_FORMAT.dayMonthYear });
  });

  return {
    showPlanSelectionButton,
    сanCreateSubscriptions,
    subscriptionRequest,
    shortPlan,
    hasCargoOwnerSubscription,
    expectedSubscriptionDate,
    subscriptionWithPendingStatus,
    hasCarrierStartSubscription,
  };
};
